import React, { useState } from 'react';
import { MessageCircle, Edit2, X } from 'lucide-react';
import { NoteAnnotation } from '../types/notes';

const shortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

interface MarginNotesProps {
  annotations: NoteAnnotation[];
  /** Omit to render read-only (e.g. someone else's public note). */
  onDelete?: (annotation: NoteAnnotation) => Promise<void>;
}

/**
 * Margin notes pinned to this note from chat — the "living" layer on top of the
 * transcription (mobile's MarginNotes). Each is a slip of paper tucked behind
 * the page: a violet rule down its edge, the quoted line, then the thought.
 * Renders nothing when empty.
 */
export const MarginNotes: React.FC<MarginNotesProps> = ({ annotations, onDelete }) => {
  const [deleting, setDeleting] = useState<number | null>(null);
  if (annotations.length === 0) return null;

  const handleDelete = async (a: NoteAnnotation): Promise<void> => {
    if (!onDelete || !window.confirm('Erase this margin note? Future chats won’t see it either.')) return;
    setDeleting(a.id);
    try {
      await onDelete(a);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <section aria-label="Margin notes" className="mt-8 flex flex-col gap-3">
      <header className="flex items-center justify-between px-1">
        <span className="pb-eyebrow">Margin notes</span>
        <span className="font-body text-[11px] font-semibold text-on-surface-variant">{annotations.length}</span>
      </header>
      {annotations.map((a) => (
        <div
          key={a.id}
          className={`relative overflow-hidden rounded-xl border border-solid border-outline-variant bg-surface-container-low py-3.5 pl-5 pr-4 transition-opacity ${
            deleting === a.id ? 'opacity-40' : ''
          }`}
        >
          <span aria-hidden className="absolute bottom-0 left-0 top-0 w-[3px] bg-accent opacity-55" />
          {a.quote && (
            <p className="m-0 mb-1.5 line-clamp-2 font-headline text-[0.9375rem] italic leading-6 text-on-surface-variant">
              “{a.quote}”
            </p>
          )}
          <p className="m-0 whitespace-pre-wrap font-body text-[0.984rem] leading-[1.55] text-on-surface">{a.body}</p>
          <div className="mt-2.5 flex items-center gap-1.5 text-on-surface-variant">
            {a.author === 'assistant' ? <MessageCircle size={11} /> : <Edit2 size={11} />}
            <span className="flex-1 font-body text-[11px] font-medium">
              {a.author === 'assistant' ? 'From a chat' : 'You'} · {shortDate(a.created_at)}
            </span>
            {onDelete && (
              <button
                type="button"
                onClick={() => void handleDelete(a)}
                disabled={deleting === a.id}
                aria-label="Erase margin note"
                title="Erase"
                className="flex cursor-pointer items-center rounded-full border-none bg-transparent p-1 text-on-surface-variant hover:bg-surface-container hover:text-error"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>
      ))}
    </section>
  );
};
